<script setup lang="ts">
  import BasicNavItem from '../basic/BasicNavItem.vue'
  import BasicNestedNavItem from '../basic/BasicNestedNavItem.vue'
  import { get } from 'lodash'
  import { findActiveNavigationIndex } from '~/utils/navigationActiveState'

  const { t } = useI18n()

  const props = defineProps<{
    routes: {
      name: string
      path: string
      icon?: string
      disabled?: boolean
      auth?: {
        roles: string[]
      }
      children?: {
        name: string
        path: string
        disabled?: boolean
        auth?: {
          roles: string[]
        }
        children?: {
          name: string
          path: string
          disabled?: boolean
          auth?: {
            roles: string[]
          }
        }[]
      }[]
      bold?: boolean
    }[]
  }>()

  const navigationStore = useNavigationStore()
  const currentRoute = useRoute()
  const { normalizePath } = useLocalizedPath()
  const activeRootIndex = computed(() =>
    findActiveNavigationIndex(props.routes, currentRoute.path, normalizePath),
  )
  // Group samples use My monitoring detail routes while retaining their group origin in the query.
  const isGroupMonitoringSample = computed(
    () =>
      currentRoute.query.from === 'group-monitoring' &&
      /\/monitoring-database\/my-monitoring\/[^/]+\/(?:sample|planned-sample|copied-sample)\/[^/]+(?:\/|$)/.test(
        currentRoute.path,
      ),
  )
  const nestedActiveState = (name: string): 'active' | 'inactive' | undefined => {
    if (!isGroupMonitoringSample.value) return undefined
    if (name === 'myMonitoring') return 'inactive'
    if (name === 'myGroupMonitoring') return 'active'
    return undefined
  }

  const selectedIndex = useLocalStorage<number[]>('navigationSelectedIndex', [])
  const manualShowClicked = useLocalStorage('lockNavigation2', () => false)

  const isValidSelectedIndex = (indexes: number[]) => {
    let routes = props.routes

    return indexes.length > 0
      ? indexes.every((index) => {
          if (!Number.isInteger(index) || index < 0) return false

          const route = routes[index]
          if (!route?.children?.length) return false

          routes = route.children
          return true
        })
      : false
  }

  const hasNestedSelection = computed(() => isValidSelectedIndex(selectedIndex.value))

  const selectedIndexPath = computed(() =>
    (hasNestedSelection.value ? selectedIndex.value : []).flatMap((value, index, array) => {
      return index < array.length - 1 ? [value, 'children'] : [value]
    }),
  )
  const selectedParent = computed(() => get(props.routes, selectedIndexPath.value))
  const selectedRoutes = computed(() => selectedParent.value?.children ?? [])
  const selectedParentName = computed(() => {
    if (selectedIndex.value.length <= 1) return 'mainMenu'
    return selectedParent.value?.name ?? 'mainMenu'
  })

  watch(
    [() => props.routes, selectedIndex],
    () => {
      if (selectedIndex.value.length > 0 && !isValidSelectedIndex(selectedIndex.value)) {
        selectedIndex.value = []
      }
    },
    { deep: true, immediate: true },
  )

  const { width } = useWindowSize()

  const hover = ref(false)
  const isNavigationOpen = computed(
    () => navigationStore.lockNavigation || hover.value || navigationStore.temporaryOpenNavigation,
  )

  const openNavigationOnHover = () => {
    hover.value = true
  }

  const closeNavigationOnLeave = () => {
    hover.value = false
    navigationStore.clearTemporaryOpenNavigation()
  }

  const closeNestedNavigation = () => {
    selectedIndex.value = []
  }

  const svgVisible = ref(false)

  const toggleSvg = () => {
    svgVisible.value = true

    setTimeout(() => {
      svgVisible.value = false
    }, 500)
  }

  const { customNavigateTo } = useCustomNavigation()
  const navigateToSupport = () => {
    const supportLink = t('nav.menu.supportLink')
    customNavigateTo(supportLink, {
      open: {
        target: '_blank',
      },
    })
  }

  watch(
    () => navigationStore.$state.lockNavigation,
    () => {
      toggleSvg()
    },
  )
</script>

<template>
  <nav
    class="duration-500 border-r flex flex-col items-stretch overflow-y-auto bg-white"
    :class="{
      'min-w-[345px] max-w-[445px]': isNavigationOpen,
      'min-w-[57px] max-w-[57px]': !isNavigationOpen,
      'min-w-[345px] max-w-[445px] _absolute z-30 bg-white bottom-0 top-20 shadow-2xl':
        width < 1280 && isNavigationOpen,
      'min-w-[57px] max-w-[57px] _absolute z-30 bg-white bottom-0 top-20':
        width < 1280 && !isNavigationOpen,
    }"
    @mouseover="openNavigationOnHover"
    @mouseleave="closeNavigationOnLeave"
  >
    <div
      class="px-3 py-2 border-b h-12 transition-all"
      :class="navigationStore.lockNavigation ? 'flex justify-between' : ''"
    >
      <button
        driver-id="navigation-lock-button"
        class="rounded-full transition-all border hover:bg-gray-500/10 bg-center bg-[length:20px_20px] w-8 h-8 bg-no-repeat duration-500"
        :class="{
          'ml-auto bg-[url(/icons/lock_24px_outlined.svg)]': navigationStore.lockNavigation,
          'ml-0 bg-[url(/icons/lock_open_24px_outlined.svg)]': !navigationStore.lockNavigation,
        }"
        @click="
          () => {
            navigationStore.toggleLockNavigation()
            manualShowClicked = true
          }
        "
      />
    </div>
    <div class="flex flex-row flex-grow">
      <ul
        id="root-nav"
        class="transition-all duration-100"
        :class="hasNestedSelection ? 'w-[57px] border-r' : 'w-full'"
      >
        <template v-for="(route, index) in props.routes" :key="index">
          <BasicNavItem
            v-if="!route.children && route.path"
            :id="route.id"
            :key="route?.id + route.path + index"
            :item="route"
            :icon-only="!isNavigationOpen || hasNestedSelection"
            :active-state="activeRootIndex === index ? 'active' : 'inactive'"
            @click="closeNestedNavigation"
          />
          <BasicNestedNavItem
            v-else-if="route.children"
            :id="route.id"
            :icon-only="!isNavigationOpen || hasNestedSelection"
            :item="route"
            :selected="selectedIndex[0] == index"
            :active-state="activeRootIndex === index ? 'active' : 'inactive'"
            :bold="route.bold"
            @click="
              () => {
                selectedIndex = [index]
              }
            "
          />
          <BasicNavItem
            v-else
            :id="route.id"
            :item="route"
            :icon-only="!isNavigationOpen || hasNestedSelection"
            :active-state="activeRootIndex === index ? 'active' : 'inactive'"
            @click="closeNestedNavigation"
          />
        </template>
      </ul>
      <ul
        v-if="hasNestedSelection"
        id="nested"
        class="w-full overflow-auto max-h-[calc(100vh-2rem)]"
      >
        <BasicNavItem
          :item="{
            name: selectedParentName,
            icon: 'ic:baseline-chevron-right',
            path: '#',
          }"
          flip
          @click="selectedIndex.pop()"
        />
        <template v-for="(route, index) in selectedRoutes" :key="index">
          <BasicNavItem
            v-if="!route.children && route.path"
            v-show="isNavigationOpen"
            :item="route"
            :active-state="nestedActiveState(route.name)"
          />
          <BasicNestedNavItem
            v-else-if="route.children"
            v-show="isNavigationOpen"
            has-parent
            :bold="route.bold"
            :item="route"
            @click="selectedIndex.push(index)"
          />
        </template>
      </ul>
    </div>
    <div class="block border-t py-4 px-3">
      <!--      todo: help icon-->
      <div
        class="flex justify-center items-center h-8 bg-affirmative hover:bg-affirmative-light rounded-full cursor-pointer"
        :class="{}"
        @click="navigateToSupport"
      >
        <img src="/icons/help-24px-white.svg" class="w-5 h-5" />
        <p v-if="isNavigationOpen" class="opacity-100 transition-opacity text-white ml-2">
          {{ t('nav.menu.support') }}
        </p>
      </div>
    </div>
  </nav>

  <ClientOnly>
    <Teleport to="body">
      <div
        class="fixed inset-0 flex flex-col items-center justify-center pointer-events-none transition-all ease-in-out duration-500 z-[999] bg-white/30"
        :class="{
          'opacity-0': !svgVisible,
          'opacity-100': svgVisible,
        }"
      >
        <img
          :src="
            navigationStore.lockNavigation
              ? `/icons/lock_24px_outlined.svg`
              : '/icons/lock_open_24px_outlined.svg'
          "
          class="w-20 h-20"
        />
        <p class="text-primary-light">
          {{ navigationStore.lockNavigation ? 'Locked' : 'Unlocked' }} menu position
        </p>
      </div>
    </Teleport>
  </ClientOnly>
</template>
